import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { theme as appTheme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { IGlobalConfigurationUpdate } from '../../redux/configuration/types';
import type { ContentScrubEditorState } from '../../types/global/contentScrub.types';
import { dedupeStrings, normalizeLinkDomain, normalizePlatformName } from '../../utils/contentScrubUtils';

const accordionSx = (themeStyle: keyof typeof appTheme) => ({
    backgroundColor: 'transparent',
    color: appTheme[themeStyle].tag_white,
    '& .MuiAccordionSummary-root': { color: appTheme[themeStyle].tag_white },
    '& .MuiAccordionDetails-root': { color: appTheme[themeStyle].tag_white },
});

const fieldSx = (themeStyle: keyof typeof appTheme) => ({
    '& .MuiOutlinedInput-root': {
        color: appTheme[themeStyle].tag_white,
        '& fieldset': { borderColor: appTheme[themeStyle].form_divider },
        '&:hover fieldset': { borderColor: appTheme[themeStyle].highlight },
    },
});

function ReadOnlyChipList({ items, themeStyle }: { items: string[]; themeStyle: keyof typeof appTheme }) {
    if (items.length === 0) {
        return (
            <Typography sx={{ color: appTheme[themeStyle].grey, fontSize: '0.75rem', fontStyle: 'italic' }}>
                None
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {items.map(item => (
                <Chip
                    key={item}
                    label={item}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.06)',
                        color: appTheme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        border: `1px solid ${appTheme[themeStyle].form_divider}`,
                    }}
                />
            ))}
        </Box>
    );
}

function TagListField({
    label,
    description,
    placeholder,
    items,
    onChange,
    normalize,
    invalidMessage,
    themeStyle,
}: {
    label: string;
    description: string;
    placeholder: string;
    items: string[];
    onChange: (items: string[]) => void;
    normalize: (input: string) => string | null;
    invalidMessage: string;
    themeStyle: keyof typeof appTheme;
}) {
    const [input, setInput] = useState('');
    const [error, setError] = useState('');

    const addItem = () => {
        const normalized = normalize(input);
        if (!normalized) {
            setError(invalidMessage);
            return;
        }
        if (items.some(item => item.toLowerCase() === normalized)) {
            setError(`"${normalized}" is already in the list`);
            return;
        }
        onChange(dedupeStrings([...items, normalized]));
        setInput('');
        setError('');
    };

    const removeItem = (index: number) => {
        onChange(items.filter((_, i) => i !== index));
    };

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 1,
                border: `1px solid ${appTheme[themeStyle].form_divider}`,
            }}>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: appTheme[themeStyle].tag_white, mb: 0.5 }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    color: appTheme[themeStyle].grey,
                    fontSize: '0.75rem',
                    mb: 2,
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                }}>
                {description}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={placeholder}
                    value={input}
                    onChange={e => {
                        setInput(e.target.value);
                        if (error) setError('');
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addItem();
                        }
                    }}
                    error={Boolean(error)}
                    helperText={error}
                    sx={fieldSx(themeStyle)}
                />
                <IconButton
                    onClick={addItem}
                    aria-label={`Add ${label}`}
                    sx={{ color: appTheme[themeStyle].highlight, flexShrink: 0 }}>
                    <AddIcon />
                </IconButton>
            </Box>

            {items.length > 0 ? (
                <Stack spacing={0.75}>
                    {items.map((item, idx) => (
                        <Box
                            key={`${item}-${idx}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.75,
                                px: 1,
                                borderRadius: 1,
                                bgcolor: 'rgba(255,255,255,0.04)',
                            }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14 }}>{item}</Typography>
                            <IconButton
                                size="small"
                                onClick={() => removeItem(idx)}
                                sx={{ color: appTheme[themeStyle].grey }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Typography sx={{ color: appTheme[themeStyle].grey, fontSize: '0.75rem', fontStyle: 'italic' }}>
                    No additional entries — code defaults still apply.
                </Typography>
            )}
        </Box>
    );
}

export interface GlobalConfigurationContentScrubEditorProps {
    config: IGlobalConfigurationUpdate;
    setConfig: React.Dispatch<React.SetStateAction<IGlobalConfigurationUpdate>>;
    setHasChanges: (changed: boolean) => void;
    themeStyle: keyof typeof appTheme;
}

export function GlobalConfigurationContentScrubEditor({
    config,
    setConfig,
    setHasChanges,
    themeStyle,
}: GlobalConfigurationContentScrubEditorProps) {
    const scrub: ContentScrubEditorState = config.content_scrub ?? {
        allowed_link_domains: [],
        allowed_platform_names: [],
        scrub_platform_names: [],
        default_allowed_link_domains: [],
        default_scrub_platform_names: [],
    };

    const updateScrub = (patch: Partial<ContentScrubEditorState>) => {
        setConfig({
            ...config,
            content_scrub: { ...scrub, ...patch },
        });
        setHasChanges(true);
    };

    return (
        <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    HTML content scrub
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography
                    sx={{
                        color: appTheme[themeStyle].grey,
                        fontSize: '0.75rem',
                        mb: 2,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    User-submitted HTML in auditions, roles, contests, profiles, and events is scrubbed on save. Regular
                    users lose all links and platform names; admin authors keep links on allowed domains only. Phone
                    numbers are always hidden. Scrub runs on write — existing content is unchanged until edited.
                </Typography>

                <Stack spacing={3}>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 1,
                            border: `1px solid ${appTheme[themeStyle].form_divider}`,
                            bgcolor: 'rgba(255,255,255,0.02)',
                        }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, mb: 1 }}>
                            Built-in defaults (read-only)
                        </Typography>
                        <Typography sx={{ color: appTheme[themeStyle].grey, fontSize: '0.75rem', mb: 2 }}>
                            Always merged server-side — cannot be removed via PATCH.
                        </Typography>

                        <Typography sx={{ fontSize: '0.8rem', fontFamily: FONT_ENUM.MAIN_BOLD, mb: 1, mt: 1 }}>
                            Allowed link domains (admin / super-admin authors)
                        </Typography>
                        <ReadOnlyChipList items={scrub.default_allowed_link_domains} themeStyle={themeStyle} />

                        <Typography sx={{ fontSize: '0.8rem', fontFamily: FONT_ENUM.MAIN_BOLD, mb: 1, mt: 2 }}>
                            Scrubbed platform names (all authors)
                        </Typography>
                        <ReadOnlyChipList items={scrub.default_scrub_platform_names} themeStyle={themeStyle} />
                    </Box>

                    <TagListField
                        label="Additional allowed link domains"
                        description="Hostnames only (e.g. vimeo.com). Links to these domains are kept for admin / super-admin HTML; regular users still lose all links."
                        placeholder="vimeo.com"
                        items={scrub.allowed_link_domains}
                        onChange={allowed_link_domains => updateScrub({ allowed_link_domains })}
                        normalize={normalizeLinkDomain}
                        invalidMessage="Enter a valid hostname only — no https:// or paths (e.g. vimeo.com)"
                        themeStyle={themeStyle}
                    />

                    <TagListField
                        label="Additional allowed platform names"
                        description="Platform names listed here are never scrubbed, even if they also appear in the scrub list."
                        placeholder="vimeo"
                        items={scrub.allowed_platform_names}
                        onChange={allowed_platform_names => updateScrub({ allowed_platform_names })}
                        normalize={normalizePlatformName}
                        invalidMessage="Letters and numbers only (e.g. vimeo, whatsapp)"
                        themeStyle={themeStyle}
                    />

                    <TagListField
                        label="Additional scrubbed platform names"
                        description="Added on top of built-in defaults (e.g. telegram, whatsapp). Replaced with [platform removed] for all authors unless allowed above."
                        placeholder="telegram"
                        items={scrub.scrub_platform_names}
                        onChange={scrub_platform_names => updateScrub({ scrub_platform_names })}
                        normalize={normalizePlatformName}
                        invalidMessage="Letters and numbers only (e.g. telegram, whatsapp)"
                        themeStyle={themeStyle}
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}
